import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import GridLayout, { Layout, LayoutItem, useContainerWidth, verticalCompactor } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useDashboardLayout, useUpdateDashboardLayout, useSaveDashboardLayout, type WidgetConfig, type DashboardGroup } from './hooks/useDashboardLayout';
import { getWidgetDefinition, createWidgetElement, isKnownWidgetType } from './widgetRegistry';
import DraggableWidget from './components/DraggableWidget';
import DashboardSettings from './components/DashboardSettings';
import DashboardFilterBar from './components/DashboardFilterBar';
import SaveAsDashboardModal from './components/SaveAsDashboardModal';
import { useDashboardFilters } from './contexts/DashboardFiltersContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSettings } from '../../contexts/SettingsContext';
import { gridDotStyle, sectionTitleClass } from './themeHelpers';

export interface DynamicDashboardProps {
  layoutName?: string;
}
const ROW_HEIGHT = 120;
/** [horizontal gap between widgets, vertical gap] */
const MARGIN: [number, number] = [12, 12];
const CONTAINER_PADDING: [number, number] = [12, 12];

/** Responsive column count; never exceeds configured dashboard columns. */
function getResponsiveCols(containerWidth: number, maxCols: number): number {
  if (containerWidth <= 0) return maxCols;
  let cols = maxCols;
  if (containerWidth < 640) cols = 4;
  else if (containerWidth < 960) cols = 6;
  else if (containerWidth < 1280) cols = 9;
  return Math.min(cols, maxCols);
}

/** Build grid layout for display, scaled from saved columns to current responsive columns. */
function buildDisplayLayout(
  widgets: WidgetConfig[],
  fromCols: number,
  toCols: number,
): Layout {
  const canonical = widgets.map((w) => ({
    i: w.id,
    x: w.position?.x ?? 0,
    y: w.position?.y ?? 0,
    w: w.size?.w ?? 2,
    h: w.size?.h ?? 2,
  })) as Layout;
  return scaleLayoutToCols(canonical, fromCols, toCols);
}

/** Scale layout from current (responsive) cols to canonical cols for saving (keeps layout consistent across breakpoints). */
function scaleLayoutToCols(layout: Layout, fromCols: number, toCols: number): Layout {
  if (fromCols === toCols) return layout;
  const scale = toCols / fromCols;
  return layout.map((item) => ({
    ...item,
    x: Math.min(Math.round(item.x * scale), toCols - 1),
    y: item.y,
    w: Math.min(Math.max(1, Math.round(item.w * scale)), toCols),
    h: item.h,
  })) as Layout;
}

interface DashboardSection {
  groupId: string;
  title: string;
  widgets: WidgetConfig[];
}

const DynamicDashboard: React.FC<DynamicDashboardProps> = ({ layoutName }) => {
  const isMobile = false;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { colors, theme } = useTheme();
  const { appConfig } = useSettings();
  const COLUMNS = appConfig.dashboard.gridColumns;
  const { width, containerRef, mounted, measureWidth } = useContainerWidth({
    initialWidth: 1,
  });
  const responsiveCols = useMemo(() => getResponsiveCols(width, COLUMNS), [width, COLUMNS]);
  const { layout, isLoading, mutate } = useDashboardLayout(layoutName);

  const saveLayout = useSaveDashboardLayout();
  const updateLayout = useUpdateDashboardLayout();
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const { mutate: globalMutate } = useSWRConfig();

  // When user selects a different dashboard (URL/layoutName changes), reload from backend (DashboardLayout)
  React.useEffect(() => {
    mutate();
  }, [layoutName, mutate]);

  /** Revalidate dashboard layout and all widget data (stats, lists, charts, etc.) */
  const handleRefresh = useCallback(() => {
    mutate();
    globalMutate((key) => typeof key === 'string' && key.startsWith('/dashboard/'));
  }, [mutate, globalMutate]);

  const customizeGridStyle = useMemo(() => gridDotStyle(theme), [theme]);
  const { timeRange, scope } = useDashboardFilters();
  const [isEditing, setIsEditing] = useState(false);
  const [editingWidgetId, setEditingWidgetId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const dashboardFilters = useMemo(() => ({ timeRange, scope }), [timeRange, scope]);

  // When switching dashboard by name, only use layout if it matches the requested name (avoid showing wrong widgets)
  const layoutMatchesRequest =
    !layoutName || !layout?.name || layout.name === layoutName;
  const effectiveLayout = layoutMatchesRequest ? layout : undefined;
  const effectiveWidgets: WidgetConfig[] = useMemo(() => {
    if (!effectiveLayout?.widgets || !Array.isArray(effectiveLayout.widgets)) return [];
    return effectiveLayout.widgets
      .filter((w) => isKnownWidgetType(w.widgetType))
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [effectiveLayout?.widgets]);
  const effectiveGroups: DashboardGroup[] = useMemo(() => {
    const g = effectiveLayout?.groups;
    return Array.isArray(g) ? g : [];
  }, [effectiveLayout?.groups]);
  const effectiveSections: DashboardSection[] = useMemo(() => {
    const result: DashboardSection[] = [];
    for (const g of effectiveGroups) {
      result.push({
        groupId: g.id,
        title: g.title,
        widgets: effectiveWidgets.filter((w) => w.groupId === g.id),
      });
    }
    const ungrouped = effectiveWidgets.filter((w) => !w.groupId);
    if (ungrouped.length > 0) {
      result.push({ groupId: '', title: 'Other widgets', widgets: ungrouped });
    }
    return result;
  }, [effectiveGroups, effectiveWidgets]);

  const handleSaveAsDashboard = useCallback(
    (name: string) => {
      const widgets = effectiveLayout?.widgets ?? layout?.widgets ?? [];
      const groups = effectiveLayout?.groups ?? layout?.groups ?? [];
      saveLayout({ name, widgets, groups, isDefault: false })
        .then(() => {
          const next = new URLSearchParams(searchParams);
          next.set('dashboard', name);
          navigate({ pathname: '/dashboard', search: next.toString() });
        })
        .catch(() => {});
    },
    [effectiveLayout?.widgets, effectiveLayout?.groups, layout?.widgets, layout?.groups, saveLayout, navigate, searchParams]
  );

  const gridLayout: Layout = useMemo(
    () => buildDisplayLayout(effectiveWidgets, COLUMNS, responsiveCols),
    [effectiveWidgets, COLUMNS, responsiveCols],
  );

  // Re-measure when the dashboard container or window size changes
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !measureWidth) return undefined;

    measureWidth();
    const raf = requestAnimationFrame(() => measureWidth());
    const t = window.setTimeout(() => measureWidth(), 100);

    const ro = new ResizeObserver(() => measureWidth());
    ro.observe(el);

    const onWindowResize = () => measureWidth();
    window.addEventListener('resize', onWindowResize);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
      ro.disconnect();
      window.removeEventListener('resize', onWindowResize);
    };
  }, [containerRef, measureWidth, layoutName, effectiveWidgets.length]);

  const onLayoutChange = useCallback(
    (newLayout: Layout) => {
      if (!effectiveLayout?.id || !isEditing) return;
      const scaled = scaleLayoutToCols(newLayout as LayoutItem[], responsiveCols, COLUMNS);
      const layoutArray = Array.from(scaled) as LayoutItem[];
      const updated: WidgetConfig[] = effectiveWidgets.map((w) => {
        const item = layoutArray.find((l: LayoutItem) => l.i === w.id);
        if (!item) return w;
        return {
          ...w,
          position: { x: item.x, y: item.y },
          size: { w: item.w, h: item.h },
        };
      });
      updateLayout(effectiveLayout!.id, { widgets: updated }).catch(() => {});
    },
    [effectiveLayout?.id, isEditing, effectiveWidgets, updateLayout, responsiveCols, COLUMNS]
  );

  const onSectionLayoutChange = useCallback(
    (sectionWidgets: WidgetConfig[], newLayout: Layout) => {
      if (!effectiveLayout?.id || !isEditing) return;
      const scaled = scaleLayoutToCols(newLayout as LayoutItem[], responsiveCols, COLUMNS);
      const layoutArray = Array.from(scaled) as LayoutItem[];
      const sectionIds = new Set(sectionWidgets.map((w) => w.id));
      const updated: WidgetConfig[] = effectiveWidgets.map((w) => {
        if (!sectionIds.has(w.id)) return w;
        const item = layoutArray.find((l: LayoutItem) => l.i === w.id);
        if (!item) return w;
        return {
          ...w,
          position: { x: item.x, y: item.y },
          size: { w: item.w, h: item.h },
        };
      });
      updateLayout(effectiveLayout!.id, { widgets: updated }).catch(() => {});
    },
    [effectiveLayout?.id, isEditing, effectiveWidgets, updateLayout, responsiveCols, COLUMNS]
  );

  const handleRemove = useCallback(
    (widgetId: string) => {
      if (!effectiveLayout?.id) return;
      const updated = effectiveWidgets.filter((w) => w.id !== widgetId);
      updateLayout(effectiveLayout.id, { widgets: updated }).then(() => mutate()).catch(() => {});
    },
    [effectiveLayout?.id, effectiveWidgets, updateLayout, mutate]
  );

  const handleSettingsClick = useCallback((widgetId: string) => {
    setEditingWidgetId(widgetId);
    setSettingsOpen(true);
  }, []);

  const openSettings = useCallback(() => {
    setEditingWidgetId(null);
    setSettingsOpen(true);
  }, []);

  const closeSettings = useCallback(() => {
    setSettingsOpen(false);
    setEditingWidgetId(null);
  }, []);

  const dragHandle = (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M8 6h2v2H8V6zm0 5h2v2H8v-2zm0 5h2v2H8v-2zm5-10h2v2h-2V6zm0 5h2v2h-2v-2zm0 5h2v2h-2v-2z" />
    </svg>
  );

  if (isLoading && !effectiveLayout) {
    return (
      <div className="p-4 flex items-center justify-center min-h-[200px]">
        <div className={`animate-pulse ${colors.textMuted}`}>Loading dashboard...</div>
      </div>
    );
  }

  const hasWidgets = effectiveWidgets.length > 0;

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col">
      <div className="w-full px-3 pb-2 pt-1.5 sm:px-3 sm:pb-2 sm:pt-2">
        <DashboardFilterBar
          currentLayoutName={layoutName}
          onSaveAsDashboard={() => setSaveAsOpen(true)}
          onManageDashboards={() => setSettingsOpen(true)}
          onRefresh={handleRefresh}
          isCustomizing={isEditing}
          onCustomizeToggle={() => setIsEditing((e) => !e)}
          onOpenSettings={openSettings}
        />
      </div>
      <SaveAsDashboardModal
        isOpen={saveAsOpen}
        onClose={() => setSaveAsOpen(false)}
        onConfirm={handleSaveAsDashboard}
        suggestedName={effectiveLayout?.name ? `${effectiveLayout.name} (copy)` : layout?.name ? `${layout.name} (copy)` : ''}
      />
      <div className="dashboard-scrollbar flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-auto px-3 pb-3 sm:px-0 sm:pb-0">
      {!hasWidgets ? (
        <div
          className={`p-8 text-center border border-dashed rounded-xl min-h-[300px] flex items-center justify-center ${colors.textMuted} ${colors.border} ${isEditing ? '' : ''}`}
          style={isEditing ? customizeGridStyle : undefined}
        >
          <p>No widgets yet. Use the buttons below to add widgets or customize.</p>
        </div>
      ) : isMobile ? (
        <div className="w-full min-w-0 space-y-0 px-0 pb-0">
          {effectiveGroups.length > 0
            ? effectiveSections.map((sec) => (
                <div key={sec.groupId || 'other'}>
                  <h2 className={`${sectionTitleClass(colors)} mb-3 mt-5 first:mt-0`}>{sec.title}</h2>
                  <div className="space-y-3">
                    {sec.widgets.map((w) => {
                      const def = getWidgetDefinition(w.widgetType);
                      const title = def?.name ?? w.widgetType;
                      const content = createWidgetElement(w.widgetType, w.settings ?? {}, isEditing, dashboardFilters);
                      return (
                        <div key={w.id}>
                          <DraggableWidget
                            id={w.id}
                            title={title}
                            isEditing={isEditing}
                            onRemove={isEditing ? () => handleRemove(w.id) : undefined}
                            onSettingsClick={isEditing ? () => handleSettingsClick(w.id) : undefined}
                            dashboardFilterSupport={def?.dashboardFilterSupport}
                          >
                            {content ?? <span className={colors.textMuted}>Unknown widget: {w.widgetType}</span>}
                          </DraggableWidget>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            : effectiveWidgets.map((w) => {
                const def = getWidgetDefinition(w.widgetType);
                const title = def?.name ?? w.widgetType;
                const content = createWidgetElement(w.widgetType, w.settings ?? {}, isEditing, dashboardFilters);
                return (
                  <div key={w.id}>
                    <DraggableWidget
                      id={w.id}
                      title={title}
                      isEditing={isEditing}
                      onRemove={isEditing ? () => handleRemove(w.id) : undefined}
                      onSettingsClick={isEditing ? () => handleSettingsClick(w.id) : undefined}
                      dashboardFilterSupport={def?.dashboardFilterSupport}
                    >
                      {content ?? <span className={colors.textMuted}>Unknown widget: {w.widgetType}</span>}
                    </DraggableWidget>
                  </div>
                );
              })}
        </div>
      ) : effectiveGroups.length > 0 ? (
        <div
          ref={containerRef as React.RefObject<HTMLDivElement>}
          className={`min-h-full w-full min-w-0 flex-1 space-y-4 rounded-none ${isEditing ? 'py-2' : ''}`}
          style={isEditing ? customizeGridStyle : undefined}
        >
          {effectiveSections.map((sec) => {
            const sectionLayout = buildDisplayLayout(sec.widgets, COLUMNS, responsiveCols);
            const sectionLayoutById = new Map(sectionLayout.map((item) => [item.i, item]));
            return (
              <section key={sec.groupId || 'other'} className="min-h-[120px]">
                <h2 className={sectionTitleClass(colors)}>{sec.title}</h2>
                {sec.widgets.length === 0 ? null : mounted && width > 0 ? (
                  <GridLayout
                    className="layout"
                    width={width}
                    layout={sectionLayout}
                    onLayoutChange={(l) => onSectionLayoutChange(sec.widgets, l)}
                    gridConfig={{
                      cols: responsiveCols,
                      rowHeight: ROW_HEIGHT,
                      margin: MARGIN,
                      containerPadding: CONTAINER_PADDING,
                      maxRows: Infinity,
                    }}
                    dragConfig={{
                      enabled: isEditing,
                      bounded: false,
                      handle: '.drag-handle',
                      threshold: 3,
                    }}
                    resizeConfig={{
                      enabled: isEditing,
                      handles: ['se', 's', 'e'],
                    }}
                    compactor={verticalCompactor}
                  >
                    {sec.widgets.map((w) => {
                      const def = getWidgetDefinition(w.widgetType);
                      const title = def?.name ?? w.widgetType;
                      const content = createWidgetElement(w.widgetType, w.settings ?? {}, isEditing, dashboardFilters);
                      const layoutItem = sectionLayoutById.get(w.id);
                      return (
                        <div key={w.id} className="w-full h-full min-w-0 min-h-0" data-grid={{ x: layoutItem?.x, y: layoutItem?.y, w: layoutItem?.w, h: layoutItem?.h }}>
                          <DraggableWidget
                            id={w.id}
                            title={title}
                            isEditing={isEditing}
                            onRemove={isEditing ? () => handleRemove(w.id) : undefined}
                            onSettingsClick={isEditing ? () => handleSettingsClick(w.id) : undefined}
                            dragHandle={isEditing ? <span className="drag-handle">{dragHandle}</span> : undefined}
                            dashboardFilterSupport={def?.dashboardFilterSupport}
                          >
                            {content ?? <span className={colors.textMuted}>Unknown widget: {w.widgetType}</span>}
                          </DraggableWidget>
                        </div>
                      );
                    })}
                  </GridLayout>
                ) : null}
              </section>
            );
          })}
        </div>
      ) : (
        <div
          ref={containerRef as React.RefObject<HTMLDivElement>}
          className={`min-h-full w-full min-w-0 flex-1 rounded-none ${isEditing ? 'py-2' : ''}`}
          style={isEditing ? customizeGridStyle : undefined}
        >
          {mounted && width > 0 && (
            <GridLayout
              className="layout"
              width={width}
              layout={gridLayout}
              onLayoutChange={onLayoutChange}
              gridConfig={{
                cols: responsiveCols,
                rowHeight: ROW_HEIGHT,
                margin: MARGIN,
                containerPadding: CONTAINER_PADDING,
                maxRows: Infinity,
              }}
              dragConfig={{
                enabled: isEditing,
                bounded: false,
                handle: '.drag-handle',
                threshold: 3,
              }}
              resizeConfig={{
                enabled: isEditing,
                handles: ['se', 's', 'e'],
              }}
              compactor={verticalCompactor}
            >
          {effectiveWidgets.map((w) => {
            const def = getWidgetDefinition(w.widgetType);
            const title = def?.name ?? w.widgetType;
            const content = createWidgetElement(w.widgetType, w.settings ?? {}, isEditing, dashboardFilters);
            const layoutItem = gridLayout.find((item) => item.i === w.id);
            return (
              <div key={w.id} className="w-full h-full min-w-0 min-h-0" data-grid={{ x: layoutItem?.x, y: layoutItem?.y, w: layoutItem?.w, h: layoutItem?.h }}>
                <DraggableWidget
                  id={w.id}
                  title={title}
                  isEditing={isEditing}
                  onRemove={isEditing ? () => handleRemove(w.id) : undefined}
                  onSettingsClick={isEditing ? () => handleSettingsClick(w.id) : undefined}
                  dragHandle={isEditing ? <span className="drag-handle">{dragHandle}</span> : undefined}
                  dashboardFilterSupport={def?.dashboardFilterSupport}
                >
                  {content ?? <span className={colors.textMuted}>Unknown widget: {w.widgetType}</span>}
                </DraggableWidget>
              </div>
            );
          })}
        </GridLayout>
          )}
        </div>
      )}
      </div>

      <DashboardSettings
        isOpen={settingsOpen}
        onClose={closeSettings}
        layoutName={layoutName}
        editingWidgetId={editingWidgetId}
        onClearEditingWidget={() => setEditingWidgetId(null)}
      />
    </div>
  );
};

export default DynamicDashboard;
